import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import {
  getAllUsers,
  updateUserEmailStatus,
  updateUserStatus,
  userSelector,
} from "src/store/slices/userSlice";
import { Grid, Typography } from "@mui/material";
import StatusDataGrid, { statusColumn } from "src/components/StatusDataGrid";
import {
  emailVerificationStatusChanges,
  emailVerificationStatusColors,
  emailVerificationStatusLabels,
  userStatus,
  userStatusChanges,
  userStatusColors,
  userStatusLabels,
  userTypes,
} from "src/constants";

const columns = [
  {
    field: "dateAdded",
    headerName: "Date added",
    type: "string",
    width: 130,
  },
  {
    field: "id",
    headerName: "ID",
    type: "string",
    width: 230,
  },
  {
    field: "firstName",
    headerName: "First name",
    width: 130,
  },
  {
    field: "lastName",
    headerName: "Last name",
    width: 130,
  },
  {
    field: "email",
    headerName: "Email",
    width: 250,
  },
  {
    field: "phoneNumber",
    headerName: "Phone number",
    width: 150,
  },
  {
    field: "organisation",
    headerName: "Organisation",
    width: 260,
  },
  {
    field: "userType",
    headerName: "User type",
    width: 130,
  },
  statusColumn({
    field: "status",
    headerName: "User status",
    labels: userStatusLabels,
    colors: userStatusColors,
    width: 190,
  }),
  statusColumn({
    field: "emailStatus",
    headerName: "Email status",
    labels: emailVerificationStatusLabels,
    colors: emailVerificationStatusColors,
    width: 170,
  }),
];

// Admins only see their own organisation's users
const adminColumns = columns.filter(({ field }) => field !== "organisation");

// What master users change from a user's status and email status dropdowns, and how
const masterStatusChanges = {
  status: userStatusChanges,
  emailStatus: emailVerificationStatusChanges,
};

const statusUpdates = {
  status: updateUserStatus,
  emailStatus: updateUserEmailStatus,
};

export default function UserList() {
  const dispatch = useDispatch();
  const { gettingAllUsers, users, userType } = useSelector(userSelector);
  const isMaster = userType === userTypes.MASTER;

  const [rows, setRows] = useState([]);

  useEffect(() => {
    dispatch(getAllUsers({}));
  }, []);

  useEffect(() => {
    if (users?.length > 0) {
      const userRows = users?.map((doc) => {
        const date = new Date(doc.createdAt);

        return {
          id: doc._id,
          firstName: doc.firstName,
          lastName: doc.lastName,
          email: doc?.email?.address || "-",
          phoneNumber: doc?.phoneNumber?.number || "-",
          organisation: doc?.organisation?.name || "-",
          userType: doc.userType || "-",
          // Users who registered before registrations had a status have none while they wait for approval
          status: doc.status || userStatus.PENDING_APPROVAL,
          emailStatus: doc?.email?.status,
          dateAdded: `${date.getDate()}.${
            date.getMonth() + 1
          }.${date.getFullYear()}`,
        };
      });
      setRows(userRows);
    } else {
      setRows([]);
    }
  }, [users]);

  const changeStatus = (row, field, status) =>
    dispatch(statusUpdates[field]({ userId: row.id, status })).unwrap();

  return (
    <Grid
      container
      item
      direction="column"
      spacing={2}
      style={{ marginTop: "64px", padding: "20px" }}
    >
      <Grid item>
        <Typography
          variant="h1"
          style={{ fontSize: "32px", fontWeight: 800, letterSpacing: "-1.5px" }}
        >
          Users
        </Typography>
      </Grid>
      <Grid item style={{ height: "66vh" }}>
        <StatusDataGrid
          loading={gettingAllUsers}
          onCellClick={(params, event) => {
            event.defaultMuiPrevented = true;
          }}
          rows={rows || []}
          columns={isMaster ? columns : adminColumns}
          statusChanges={isMaster ? masterStatusChanges : undefined}
          onStatusChange={changeStatus}
          pageSize={10}
          rowsPerPageOptions={[10]}
          disableSelectionOnClick
          style={{ background: "#ffffff" }}
        />
      </Grid>
    </Grid>
  );
}
